import React, { useState, useEffect } from 'react';
import { Calendar, Eye, EyeOff, AlertCircle, Fingerprint } from 'lucide-react';
import { authService } from '../utils/supabase';
import { logger } from '../utils/logger';
import { biometricAuth } from '../utils/biometricAuth';

const AuthScreen = ({ setUserType, setCurrentView, setCurrentUser }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [useMagicLink, setUseMagicLink] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometryType, setBiometryType] = useState('none');
  const [showBiometricOption, setShowBiometricOption] = useState(false);
  const [biometricEmail, setBiometricEmail] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  });

  // Check biometric availability on mount
  useEffect(() => {
    const checkBiometric = async () => {
      try {
        const { isAvailable, biometryType: type } = await biometricAuth.isAvailable();
        setBiometricAvailable(isAvailable);
        setBiometryType(type);

        // Check if biometric login is enabled for this device
        const shouldShow = await biometricAuth.shouldShowBiometricLogin();
        setShowBiometricOption(shouldShow);

        if (shouldShow) {
          setBiometricEmail(biometricAuth.getStoredEmail() || '');
        }

        logger.debug('Biometric check:', { isAvailable, type, shouldShow });
      } catch (error) {
        logger.error('Error checking biometric:', error);
      }
    };

    checkBiometric();
  }, []);

  const validateForm = () => {
    const newErrors = {};
    const trimmedEmail = formData.email.trim();

    if (!trimmedEmail) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(trimmedEmail)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Skip password validation for magic link
    if (!useMagicLink) {
      // Password validation
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (!isLogin && formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }

      // Registration-specific validation
      if (!isLogin) {
        if (!formData.firstName) {
          newErrors.firstName = 'First name is required';
        }

        if (!formData.lastName) {
          newErrors.lastName = 'Last name is required';
        }

        if (!formData.confirmPassword) {
          newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = 'Passwords do not match';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    // Validate email
    if (!formData.email) {
      setErrors({ email: 'Please enter your email address' });
      return;
    }

    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      logger.debug('Sending password reset email', { email: formData.email });
      const result = await authService.resetPassword(formData.email);
      logger.debug('Password reset result:', result);

      setResetEmailSent(true);
      setErrors({});
    } catch (error) {
      logger.error('Password reset error:', error);

      if (error.message.includes('not configured')) {
        setErrors({ general: 'Password reset is not available. Please contact support.' });
      } else if (error.message.includes('rate limit')) {
        setErrors({ general: 'Too many reset attempts. Please wait a moment and try again.' });
      } else {
        setErrors({ general: error.message || 'Failed to send reset email. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle biometric login
  const handleBiometricLogin = async () => {
    setLoading(true);
    setErrors({});

    try {
      logger.debug('Attempting biometric login...');
      const result = await biometricAuth.authenticate();

      if (result.cancelled) {
        // User cancelled, just reset loading state
        setLoading(false);
        return;
      }

      if (!result.success) {
        setErrors({ general: result.error || 'Biometric authentication failed' });
        setLoading(false);
        return;
      }

      // Use the stored credentials to sign in
      const { username: email, password } = result.credentials;
      logger.debug('Biometric verified, signing in with stored credentials...');

      const signInResult = await authService.signIn(email, password);

      if (signInResult.user && signInResult.profile) {
        logger.debug('Biometric login successful - onAuthStateChange will handle navigation');
      } else if (signInResult.user && !signInResult.profile) {
        logger.debug('User authenticated but no profile found, redirecting to onboarding...');
        setCurrentUser(signInResult.user);
        setCurrentView('onboarding');
      } else {
        setErrors({ general: 'Login failed. Please try again with email and password.' });
        // Disable biometric if credentials are invalid
        await biometricAuth.disableBiometric();
        setShowBiometricOption(false);
      }
    } catch (error) {
      logger.error('Biometric login error:', error);
      setErrors({ general: 'Biometric login failed. Please try with email and password.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setErrors({});

    // Use trimmed email for all auth operations
    const email = formData.email.trim();
    const password = formData.password;

    try {
      logger.debug('Starting authentication...', { isLogin, email, useMagicLink });

      if (isLogin) {
        if (useMagicLink) {
          // Handle magic link authentication
          logger.debug('Sending magic link...');
          const result = await authService.sendMagicLink(email);
          logger.debug('Magic link result:', result);
          setMagicLinkSent(true);
          setErrors({});
          return; // Don't proceed with normal login flow
        } else {
          // Handle password login with timeout
          logger.debug('Attempting password login...');

          const signInPromise = authService.signIn(email, password);
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Login timeout - please check your connection and try again')), 10000)
          );

          const result = await Promise.race([signInPromise, timeoutPromise]);
          logger.debug('Login result:', result);

          if (result.message) {
            // Magic link sent
            setMagicLinkSent(true);
            setErrors({});
            return;
          }

          if (result.user && result.profile) {
            // Don't set currentUser or navigate here - let onAuthStateChange in App.js handle it
            // This prevents race conditions and ensures subscription check runs before dashboard
            logger.debug('Login successful - onAuthStateChange will handle navigation');
            // The onAuthStateChange listener will call handleUserSignIn which:
            // 1. Checks subscription status
            // 2. Sets currentUser
            // 3. Navigates to dashboard
          } else if (result.user && !result.profile) {
            // User exists but no profile - needs onboarding
            // This case needs to be handled here since onboarding doesn't require subscription
            logger.debug('User authenticated but no profile found, redirecting to onboarding...');
            setCurrentUser(result.user);
            setCurrentView('onboarding');
          } else {
            // Login failed - show error
            logger.error('Login failed - no user returned');
            setErrors({ submit: 'Login failed. Please check your credentials and try again.' });
          }
        }
      } else {
        // Handle registration with Supabase
        logger.debug('Attempting registration...');
        const userData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          userType: 'customer' // Default to customer, can be changed in onboarding
        };

        const result = await authService.signUp(email, password, userData);
        logger.debug('Registration result:', result);

        if (result.user) {
          logger.debug('Auto-signing in after registration...');
          // Registration successful - since email confirmation is disabled, sign in immediately
          const signInResult = await authService.signIn(email, password);
          logger.debug('Auto sign-in result:', signInResult);

          if (signInResult.user && signInResult.profile) {
            // Don't set currentUser or navigate here - let onAuthStateChange in App.js handle it
            // This prevents race conditions and ensures subscription check runs before dashboard
            logger.debug('Registration + auto sign-in successful - onAuthStateChange will handle navigation');
            // The onAuthStateChange listener will call handleUserSignIn which handles everything
          } else {
            // Registration successful but no profile yet, go to onboarding
            // This case needs to be handled here since onboarding doesn't require subscription
            logger.debug('No profile found, redirecting to onboarding...');
            setCurrentUser(result.user);
            setCurrentView('onboarding');
          }
        }
      }
    } catch (error) {
      logger.error('Authentication error:', error);
      logger.error('Error details:', error);
      
      // Handle specific Supabase errors
      if (error.message.includes('Invalid login credentials')) {
        setErrors({ general: 'Invalid email or password' });
      } else if (error.message.includes('User already registered') || error.message.includes('already exists')) {
        setErrors({ general: 'An account with this email already exists. Please try signing in instead.' });
      } else if (error.message.includes('Password should be at least 6 characters')) {
        setErrors({ password: 'Password must be at least 6 characters' });
      } else if (error.message.includes('Email rate limit exceeded')) {
        setErrors({ general: 'Too many sign up attempts. Please wait a moment and try again.' });
      } else if (error.message.includes('fetch')) {
        setErrors({ general: 'Network error. Please check your connection and try again.' });
      } else {
        setErrors({ general: error.message || 'An unexpected error occurred. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-blue-100 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mx-auto mb-4">
            <img
              src="/logo192.png"
              alt="RepConnect Logo"
              className="w-24 h-24"
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">RepConnect</h1>
          <p className="text-gray-600">Your next meeting is just a click away</p>
        </div>

        {/* Biometric Login Button - shown when biometric is enabled and available */}
        {showBiometricOption && isLogin && !magicLinkSent && (
          <div className="mb-6">
            <button
              onClick={handleBiometricLogin}
              disabled={loading}
              className={`w-full flex items-center justify-center space-x-3 py-4 px-6 rounded-xl border-2 transition-all duration-200 ${
                loading
                  ? 'bg-gray-100 border-gray-200 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 hover:border-blue-400 hover:shadow-md'
              }`}
            >
              <Fingerprint className={`w-8 h-8 ${loading ? 'text-gray-400' : 'text-blue-600'}`} />
              <div className="text-left">
                <div className={`font-semibold ${loading ? 'text-gray-500' : 'text-gray-800'}`}>
                  {loading ? 'Signing in...' : `Sign in with ${biometricAuth.getBiometryName(biometryType)}`}
                </div>
                {biometricEmail && (
                  <div className="text-sm text-gray-500">{biometricEmail}</div>
                )}
              </div>
            </button>
            <div className="text-center mt-3">
              <span className="text-gray-400 text-sm">or sign in with email</span>
            </div>
          </div>
        )}

        {/* Toggle between Sign In and Register */}
        <div className="flex mb-6 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => {
              setIsLogin(true);
              setErrors({});
              setFormData({ email: '', password: '', confirmPassword: '', firstName: '', lastName: '' });
            }}
            className={`flex-1 py-2 px-4 rounded-md transition-colors ${
              isLogin
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Sign In
          </button>
          <button
            onClick={() => {
              setIsLogin(false);
              setErrors({});
              setFormData({ email: '', password: '', confirmPassword: '', firstName: '', lastName: '' });
            }}
            className={`flex-1 py-2 px-4 rounded-md transition-colors ${
              !isLogin
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Register
          </button>
        </div>

        {/* Magic Link Success Message */}
        {magicLinkSent && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="text-center">
              <div className="text-green-600 font-semibold mb-2">Check your email!</div>
              <p className="text-green-700 text-sm">
                We've sent a secure login link to <strong>{formData.email}</strong>
              </p>
              <p className="text-green-600 text-xs mt-2">
                Click the link in your email to sign in securely
              </p>
              <button
                onClick={() => {
                  setMagicLinkSent(false);
                  setUseMagicLink(false);
                  setFormData({ ...formData, password: '' });
                }}
                className="mt-3 text-blue-600 text-sm hover:underline"
              >
                Try a different method
              </button>
            </div>
          </div>
        )}

        {/* Magic Link Option Toggle */}
        {isLogin && !magicLinkSent && (
          <div className="mb-4 text-center">
            <button
              type="button"
              onClick={() => {
                setUseMagicLink(!useMagicLink);
                setErrors({});
                setFormData({ ...formData, password: '', confirmPassword: '' });
              }}
              className="text-blue-600 text-sm hover:underline"
            >
              {useMagicLink ? 'Use password instead' : 'Sign in with magic link (no password needed)'}
            </button>
          </div>
        )}

        {/* Error Message */}
        {errors.general && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <AlertCircle className="w-4 h-4 text-red-600 mr-2 flex-shrink-0" />
            <span className="text-red-800 text-sm">{errors.general}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* First and Last Name (Registration only) */}
          {!isLogin && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <input
                  type="text"
                  placeholder="First Name"
                  value={formData.firstName}
                  onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.firstName ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                {errors.firstName && (
                  <p className="text-red-600 text-sm mt-1">{errors.firstName}</p>
                )}
              </div>
              <div>
                <input
                  type="text"
                  placeholder="Last Name"
                  value={formData.lastName}
                  onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.lastName ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                {errors.lastName && (
                  <p className="text-red-600 text-sm mt-1">{errors.lastName}</p>
                )}
              </div>
            </div>
          )}

          {/* Email */}
          <div>
            <div className="relative">
              <input
                type="email"
                placeholder="Email address"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                autoComplete="email"
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-12 ${
                  errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
              />
            </div>
            {errors.email && (
              <p className="text-red-600 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          {/* Password (hidden for magic link) */}
          {!useMagicLink && (
            <div>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  autoComplete={isLogin ? "current-password" : "new-password"}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-12 ${
                    errors.password ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-600 text-sm mt-1">{errors.password}</p>
              )}
            </div>
          )}

          {/* Confirm Password (Registration only, hidden for magic link) */}
          {!isLogin && !useMagicLink && (
            <div>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm Password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-12 ${
                    errors.confirmPassword ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-red-600 text-sm mt-1">{errors.confirmPassword}</p>
              )}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className={`w-full font-semibold py-3 px-6 rounded-lg transition duration-200 shadow-md ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                {isLogin ? (useMagicLink ? 'Sending Magic Link...' : 'Signing In...') : 'Creating Account...'}
              </div>
            ) : (
              isLogin ? (useMagicLink ? 'Send Magic Link' : 'Sign In') : 'Create Account'
            )}
          </button>
        </form>

        {/* Forgot Password Link (Login only) */}
        {isLogin && !showForgotPassword && (
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => {
                setShowForgotPassword(true);
                setErrors({});
                setResetEmailSent(false);
              }}
              className="text-blue-600 hover:underline text-sm"
            >
              Forgot password?
            </button>
          </div>
        )}

        {/* Forgot Password Form */}
        {showForgotPassword && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            {resetEmailSent ? (
              <div className="text-center">
                <div className="text-green-600 font-semibold mb-2">Reset email sent!</div>
                <p className="text-green-700 text-sm mb-3">
                  Check your email at <strong>{formData.email}</strong> for password reset instructions.
                </p>
                <button
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetEmailSent(false);
                    setFormData({ ...formData, email: '' });
                  }}
                  className="text-blue-600 text-sm hover:underline"
                >
                  Back to sign in
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword}>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Reset Password</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Enter your email address and we'll send you a link to reset your password.
                </p>

                {/* Error Message */}
                {errors.general && (
                  <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded flex items-center">
                    <AlertCircle className="w-4 h-4 text-red-600 mr-2 flex-shrink-0" />
                    <span className="text-red-800 text-sm">{errors.general}</span>
                  </div>
                )}

                {/* Email Input */}
                <div className="mb-4">
                  <input
                    type="email"
                    placeholder="Email address"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                  {errors.email && (
                    <p className="text-red-600 text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                {/* Buttons */}
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`flex-1 font-semibold py-2 px-4 rounded-lg transition duration-200 ${
                      loading
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Sending...
                      </div>
                    ) : (
                      'Send Reset Link'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setErrors({});
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* Pricing Info */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-center text-sm text-gray-500">
            Sales Representatives: $49/month<br />
            Healthcare Providers: Always Free
          </p>
        </div>

        {/* Copyright */}
        <div className="mt-4 text-center">
          <p className="text-xs text-gray-400">
            © {new Date().getFullYear()} RepConnect, LLC and meetwithreps.com
          </p>
        </div>

        {/* Legal Links */}
        <div className="mt-3 text-center flex items-center justify-center gap-3">
          <button
            onClick={() => setCurrentView('terms-of-service')}
            className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
          >
            Terms of Service
          </button>
          <span className="text-xs text-gray-300">|</span>
          <button
            onClick={() => setCurrentView('privacy-policy')}
            className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
          >
            Privacy Policy
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthScreen;